import React, { useState, useEffect, useRef } from 'react';
import { User } from '../../services/authService';
import { KnowledgeCategoryService, KnowledgeCategory } from '../../services/knowledgeCategoryService';
import { LessonSummary } from '../../services/lessonServiceTypes';
import { MCQService, MCQQuestion } from '../../services/mcqService';
import PageHeader from '../common/PageHeader';
import './MCQPractice.css';

interface MCQPracticeProps {
  user: User | null;
  onToggleSidebar: () => void;
}

const MCQPractice: React.FC<MCQPracticeProps> = ({ user, onToggleSidebar }) => {
  const [categories, setCategories] = useState<KnowledgeCategory[]>([]);
  const [selectedCategoryId, setSelectedCategoryId] = useState<string>('');
  const [lessons, setLessons] = useState<LessonSummary[]>([]);
  const [selectedLessonId, setSelectedLessonId] = useState<string>('');
  const [numQuestions, setNumQuestions] = useState<string>('5');
  const [loading, setLoading] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [questions, setQuestions] = useState<MCQQuestion[]>([]);
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [selectedAnswers, setSelectedAnswers] = useState<number[]>([]);
  const [showResults, setShowResults] = useState(false);
  const [score, setScore] = useState(0);
  const [showMenu, setShowMenu] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  // Load categories on mount
  useEffect(() => {
    loadCategories();
  }, []);

  // Load lessons when category changes
  useEffect(() => {
    if (selectedCategoryId) {
      loadLessons(selectedCategoryId);
    } else {
      setLessons([]);
      setSelectedLessonId('');
    }
  }, [selectedCategoryId]);

  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setShowMenu(false);
      }
    };

    if (showMenu) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [showMenu]);

  const loadCategories = async () => {
    try {
      setLoading(true);
      const result = await KnowledgeCategoryService.getCategories();
      setCategories(result.categories);
    } catch (err) {
      console.error('Error loading categories:', err);
      setError(err instanceof Error ? err.message : 'Failed to load categories');
    } finally {
      setLoading(false);
    }
  };

  const loadLessons = async (categoryId: string) => {
    try {
      setLoading(true);
      const category = await KnowledgeCategoryService.getCategoryById(categoryId);
      const result = await KnowledgeCategoryService.getPastLessons(category.category_slug, 100);
      setLessons(result.lessons);
    } catch (err) {
      console.error('Error loading lessons:', err);
      setError(err instanceof Error ? err.message : 'Failed to load lessons');
    } finally {
      setLoading(false);
    }
  };

  const handleGenerateQuestions = async () => {
    const numQuestionsInt = parseInt(numQuestions, 10);
    if (isNaN(numQuestionsInt) || numQuestionsInt < 1 || numQuestionsInt > 20) {
      setError('Number of questions must be between 1 and 20');
      return;
    }

    try {
      setGenerating(true);
      setError(null);
      setQuestions([]);
      setCurrentQuestionIndex(0);
      setSelectedAnswers([]);
      setShowResults(false);
      setScore(0);

      const result = await MCQService.generateQuestions({
        ...(selectedCategoryId && { category_id: selectedCategoryId }),
        ...(selectedLessonId && { lesson_id: selectedLessonId }),
        num_questions: numQuestionsInt
      });

      setQuestions(result.questions);
    } catch (err) {
      console.error('Error generating questions:', err);
      setError(err instanceof Error ? err.message : 'Failed to generate questions. Please try again.');
    } finally {
      setGenerating(false);
    }
  };

  const handleAnswerSelect = (answerIndex: number) => {
    const newAnswers = [...selectedAnswers];
    newAnswers[currentQuestionIndex] = answerIndex;
    setSelectedAnswers(newAnswers);
  };

  const handleNextQuestion = () => {
    if (currentQuestionIndex < questions.length - 1) {
      setCurrentQuestionIndex(currentQuestionIndex + 1);
    } else {
      const correctAnswers = selectedAnswers.reduce((count, answer, index) => {
        return count + (answer === questions[index].correctAnswer ? 1 : 0);
      }, 0);
      const finalScore = Math.round((correctAnswers / questions.length) * 100);
      setScore(finalScore);
      setShowResults(true);
    }
  };

  const handleStartOver = () => {
    setQuestions([]);
    setCurrentQuestionIndex(0);
    setSelectedAnswers([]);
    setShowResults(false);
    setScore(0);
  };

  const handleReset = () => {
    setSelectedCategoryId('');
    setSelectedLessonId('');
    setNumQuestions('5');
    handleStartOver();
  };

  const currentQuestion = questions[currentQuestionIndex];
  const selectedCategory = categories.find(c => c.category_id === selectedCategoryId);
  const selectedLesson = lessons.find(l => l.lesson_id === selectedLessonId);

  return (
    <div className="mcq-practice-container">
      <PageHeader
        onToggleSidebar={onToggleSidebar}
        centerContent={
          <h1 className="mcq-practice-header-title">/mcq-practice</h1>
        }
        rightActions={
          <div className="mcq-practice-menu-container" ref={menuRef}>
            <button 
              className="page-header-icon-btn"
              onClick={() => setShowMenu(!showMenu)}
            >
              ⋮
            </button>
            {showMenu && (
              <div className="mcq-practice-menu-dropdown">
                <button
                  className="mcq-practice-menu-item"
                  onClick={handleReset}
                >
                  ↻ Reset
                </button>
              </div>
            )}
          </div>
        }
      />

      <div className="mcq-practice-content">
        {questions.length === 0 ? (
          <div className="mcq-practice-setup">
            <div className="mcq-practice-form">
              <div className="mcq-practice-form-group">
                <label className="mcq-practice-label">Category</label>
                <select
                  className="mcq-practice-select"
                  value={selectedCategoryId}
                  onChange={(e) => setSelectedCategoryId(e.target.value)}
                  disabled={loading || generating}
                >
                  <option value="">Select a category...</option>
                  {categories.map((category) => (
                    <option key={category.category_id} value={category.category_id}>
                      {category.category_name}
                    </option>
                  ))}
                </select>
              </div>

              <div className="mcq-practice-form-group">
                <label className="mcq-practice-label">Lesson</label>
                <select
                  className="mcq-practice-select"
                  value={selectedLessonId}
                  onChange={(e) => setSelectedLessonId(e.target.value)}
                  disabled={loading || generating}
                >
                  <option value="">Select a lesson...</option>
                  {lessons.map((lesson) => (
                    <option key={lesson.lesson_id} value={lesson.lesson_id}>
                      {lesson.lesson_name}
                    </option>
                  ))}
                </select>
              </div>

              <div className="mcq-practice-form-group">
                <label className="mcq-practice-label">Number of Questions</label>
                <input
                  type="number"
                  className="mcq-practice-input"
                  value={numQuestions}
                  onChange={(e) => setNumQuestions(e.target.value)}
                  min="1"
                  max="20"
                  disabled={generating}
                />
              </div>

              {error && (
                <div className="mcq-practice-error">
                  {error}
                </div>
              )}

              <button
                className="mcq-practice-generate-btn"
                onClick={handleGenerateQuestions}
                disabled={generating || loading}
              >
                {generating ? 'Generating Questions...' : 'Generate Questions'}
              </button>
            </div>
          </div>
        ) : showResults ? (
          <div className="mcq-practice-results">
            <h2 className="mcq-practice-results-title">Quiz Results</h2>
            <div className="mcq-practice-score-display">
              <span className="mcq-practice-score-value">{score}%</span>
              <span className="mcq-practice-score-label">
                {selectedAnswers.filter((answer, index) => answer === questions[index].correctAnswer).length} / {questions.length} correct
              </span>
            </div>
            <div className="mcq-practice-results-actions">
              <button className="mcq-practice-btn" onClick={handleStartOver}>
                Generate New Questions
              </button>
              <button className="mcq-practice-btn" onClick={handleReset}>
                Start Over
              </button>
            </div>
          </div>
        ) : (
          <div className="mcq-practice-quiz">
            <div className="mcq-practice-quiz-header">
              <div className="mcq-practice-quiz-info">
                {selectedCategory && (
                  <span className="mcq-practice-quiz-category">{selectedCategory.category_name}</span>
                )}
                {selectedLesson && (
                  <span className="mcq-practice-quiz-lesson">{selectedLesson.lesson_name}</span>
                )}
                {!selectedCategory && !selectedLesson && (
                  <span className="mcq-practice-quiz-category">General Knowledge</span>
                )}
              </div>
              <div className="mcq-practice-quiz-progress">
                Question {currentQuestionIndex + 1} of {questions.length}
              </div>
            </div>

            <div className="mcq-practice-question-content">
              <h3 className="mcq-practice-question-text">{currentQuestion.question}</h3>
              
              <div className="mcq-practice-answer-options">
                {currentQuestion.options.map((option: string, index: number) => (
                  <button
                    key={index}
                    className={`mcq-practice-answer-option ${
                      selectedAnswers[currentQuestionIndex] === index ? 'selected' : ''
                    }`}
                    onClick={() => handleAnswerSelect(index)}
                  >
                    {option}
                  </button>
                ))}
              </div>
            </div>
            
            <div className="mcq-practice-quiz-actions">
              <button 
                className="mcq-practice-btn"
                onClick={handleNextQuestion}
                disabled={selectedAnswers[currentQuestionIndex] === undefined}
              >
                {currentQuestionIndex === questions.length - 1 ? 'Finish Quiz' : 'Next Question'}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default MCQPractice;

